import LoadingBox from '../../components/LoadingBox';
import React, { useContext, useReducer, useEffect } from 'react';
import { Store } from '../../Store';
import { Helmet } from 'react-helmet-async';
import { getError } from '../../utils';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import userService from '../../services/user.service';
import authService from '../../services/auth.service';
import { toast } from 'react-toastify';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import PageHeader from '../../components/shared/PageHeader';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false, user: action.payload };
    default:
      return state;
  }
};

function ViewUser() {
  const { t } = useTranslation();
  const params = useParams();
  const navigate = useNavigate();

  const { id } = params;
  const [{ loading, user }, dispatch] = useReducer(reducer, {
    user: {},
    loading: true,
  });
  const { state } = useContext(Store);
  const { userInfo } = state;

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const data = await userService.getProfile(id, userInfo.token);
        dispatch({ type: 'FETCH_SUCCESS', payload: data });
      } catch (err) {
        dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchData();
  }, [id, userInfo.token]);

  const handleResetPassword = async () => {
    try {
      await authService.forgotPassword(user.email);
      toast.success(t('common.success'));
    } catch (err) {
      toast.error(getError(err));
    }
  };

  return (
    <div className='max-w-2xl mx-auto space-y-6 p-6'>
      {loading && <LoadingBox />}
      <Helmet>
        <title>{t('user.viewUser')}</title>
      </Helmet>

      <PageHeader
        title={t('user.viewUser')}
        actions={
          <Button variant='outline' size='sm' onClick={() => navigate('/AdminScreen/users')}>
            <ArrowLeft className='h-4 w-4' />
            {t('common.back')}
          </Button>
        }
      />

      <Card>
        <CardContent className='pt-6 space-y-4'>
          <div className='space-y-1'>
            <label className='text-sm font-medium text-foreground'>{t('auth.name')}</label>
            <input
              type='text'
              value={user.nombre || ''}
              disabled
              className='flex h-9 w-full rounded-md border border-input bg-muted px-3 py-1 text-sm shadow-sm opacity-70 cursor-not-allowed'
            />
          </div>

          <div className='space-y-1'>
            <label className='text-sm font-medium text-foreground'>{t('auth.email')}</label>
            <input
              type='email'
              value={user.email || ''}
              disabled
              className='flex h-9 w-full rounded-md border border-input bg-muted px-3 py-1 text-sm shadow-sm opacity-70 cursor-not-allowed'
            />
          </div>

          <div className='space-y-1'>
            <label className='text-sm font-medium text-foreground'>{t('user.phone')}</label>
            <input
              type='number'
              value={user.telefono || ''}
              disabled
              className='flex h-9 w-full rounded-md border border-input bg-muted px-3 py-1 text-sm shadow-sm opacity-70 cursor-not-allowed'
            />
          </div>

          <div className='flex items-center gap-2'>
            <input
              type='checkbox'
              id='isAdmin'
              checked={user.isAdmin || false}
              disabled
              readOnly
              className='h-4 w-4 rounded border-input accent-primary'
            />
            <label htmlFor='isAdmin' className='text-sm font-medium text-foreground'>
              {t('user.isAdmin')}
            </label>
          </div>

          <div className='flex items-center gap-2'>
            <input
              type='checkbox'
              id='isSuperAdmin'
              checked={user.isSuperAdmin || false}
              disabled
              readOnly
              className='h-4 w-4 rounded border-input accent-primary'
            />
            <label htmlFor='isSuperAdmin' className='text-sm font-medium text-foreground'>
              {t('user.isSuperAdmin')}
            </label>
          </div>

          <div className='flex items-center gap-2'>
            <input
              type='checkbox'
              id='canVerify'
              checked={user.canVerify || false}
              disabled
              readOnly
              className='h-4 w-4 rounded border-input accent-primary'
            />
            <label htmlFor='canVerify' className='text-sm font-medium text-foreground'>
              {t('user.canVerify')}
            </label>
          </div>

          <Button variant='secondary' className='w-full' onClick={handleResetPassword}>
            {t('auth.resetPassword')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

export default ViewUser;
